import {
    ProfileState,
    PlanDetailInformation,
    ProfileWizard,
    ProfileWizardSteps,
    PayrollInformation,
    ProfileInformation,
    EmployerAccounts,
    CompanyDetails,
    DashboardEnrollment,
    PlanDetailsInformation,
    ProfileEmployer,
    WexEmployerProfile,
    EmployerProfileRequest
} from "../types";
import * as UserService from "@/shared/api/userService";
import * as BankService from "@/shared/api/bankService";
import * as PayrollService from "@/shared/api/payrollService";
import {
    FetchState,
    FundingOption,
    EnrollmentOption
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as _ from "lodash";
import { BankCustodian } from "@/admin/types";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserStore } from "@/shared/store/user";

const initialWizard: ProfileWizard = {
    generalInformation: {
        complete: false,
        step: ProfileWizardSteps.GeneralInformation
    },
    payrollInformation: {
        complete: false,
        step: ProfileWizardSteps.PayrollSetup
    },
    planDetail: {
        complete: false,
        step: ProfileWizardSteps.PlanDetail
    },
    currentStep: ProfileWizardSteps.None
};

const initialProfileInformation: ProfileInformation = {
    employerId: null,
    partnerId: null,
    externalEmployerId: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    websiteUrl: "",
    mainContactPhone: "",
    employeeCount: "",
    taxId: "",
    bankCustodian: BankCustodian.Umb,
    allowedEmployerBankCustodians: [],
    isEnrollmentDisabled: false
};

const initialBankInformation: EmployerAccounts = {
    feeAccount: {
        bankRoutingNumber: "",
        bankAccountNumber: ""
    },
    authorizationDate: "",
    fundingAccount: {
        bankRoutingNumber: "",
        bankAccountNumber: ""
    }
};

const initialPayrollInformation: PayrollInformation = {
    employerId: null,
    payrollProviderName: "",
    payrollCutOffDays: 0,
    payrollCycle: "",
    dayOfWeek: "",
    firstDateOfYear: null,
    firstSemiMonthlyDay: null,
    secondSemiMonthlyDay: null,
    dayOfMonth: null,
    nextPayrollDate: null,
    nextPayrollCutOffDate: null,
    payrollCalendarLockEndDate: null,
    contact: {
        userId: null,
        name: "",
        email: "",
        phone: ""
    },
    fundingOption: null
};

const initialEnrollment: DashboardEnrollment = {
    enrolled: 0,
    inProgress: 0,
    notStarted: 0,
    percentageEnrolled: 0,
    total: 0,
    pendingInvitations: 0
};

const initialPlanDetailInformation = {
    planDetailId: null,
    employerId: null,
    planStartDate: null,
    planEndDate: null,
    contribution: {
        doesEmployerContributeToHsa: false,
        individualContributionAmountToHsa: null,
        familyContributionAmountToHsa: null,
        doesEmployerContributeUpFront: false,
        upfrontContributionPercent: null,
        areContributionsProrated: true
    }
};

const initialPlanDetailInformationList: PlanDetailInformation[] = [ initialPlanDetailInformation, initialPlanDetailInformation ];

const pristineProfileEmployer: ProfileEmployer = {
    employerInformationState: FetchState.Uninitialized,
    employerInformation: initialProfileInformation,
    bankInformationState: FetchState.Uninitialized,
    bankInformation: initialBankInformation,
    payrollInformationState: FetchState.Uninitialized,
    payrollInformation: initialPayrollInformation,
    planDetailInformationState: FetchState.Uninitialized,
    planDetailInformation: initialPlanDetailInformationList,
    profileWizardState: FetchState.Uninitialized,
    profileWizard: initialWizard,
    enrollmentMetrics: initialEnrollment,
    enrollmentState: FetchState.Uninitialized,
    isPlanDateLocked: false,
    isCurrentPlanEditLocked: false,
    fundingOption: FundingOption.None,
    fundingOptionHistory: [],
    fundingOptionHistoryState: FetchState.Uninitialized,
    enrollmentOption: EnrollmentOption.None,
    enrollmentOptionState: FetchState.Uninitialized,
    wexEmployerProfile: null,
};

export const initialState: ProfileState = {
    allEmployers: {},
    ..._.cloneDeep(pristineProfileEmployer)
};

function copyState (to: ProfileEmployer, from: ProfileEmployer) {
    to.employerInformationState = from.employerInformationState;
    to.employerInformation = from.employerInformation;
    to.bankInformationState = from.bankInformationState;
    to.bankInformation = from.bankInformation;
    to.payrollInformationState = from.payrollInformationState;
    to.payrollInformation = from.payrollInformation;
    to.planDetailInformationState = from.planDetailInformationState;
    to.planDetailInformation = from.planDetailInformation;
    to.profileWizardState = from.profileWizardState;
    to.profileWizard = from.profileWizard;
    to.enrollmentMetrics = from.enrollmentMetrics;
    to.enrollmentState = from.enrollmentState;
    to.isPlanDateLocked = from.isPlanDateLocked;
    to.isCurrentPlanEditLocked = from.isCurrentPlanEditLocked;

    return to;
}

export const useProfileStore = defineStore("profile", {
    state: (): ProfileState => (_.cloneDeep(initialState)),
    getters: {
        notifierStore () {
            return useNotifierStore();
        },
        planDetailInformationLoaded: state => state.planDetailInformationState === FetchState.Loaded,
        isPlanStartDateLocked: state => state.isPlanDateLocked,
        wizardState: state => state.profileWizardState,
        isOnboarded () {
            return this.currentStep === ProfileWizardSteps.Finish;
        },
        maxAllowedStep () {
            if (this.profileWizard.payrollInformation.complete) {
                return ProfileWizardSteps.Finish;
            } else if (this.profileWizard.planDetail.complete) {
                return ProfileWizardSteps.PayrollSetup;
            } else if (this.profileWizard.generalInformation.complete) {
                return ProfileWizardSteps.PlanDetail;
            } else {
                return ProfileWizardSteps.GeneralInformation;
            }
        },
        currentStep: state => {
            return state.profileWizard.currentStep;
        },
        isLoaded: state => {
            return state.bankInformationState === FetchState.Loaded &&
                state.employerInformationState === FetchState.Loaded;
        },
        isEmployerInformationLoaded: state => state.employerInformationState === FetchState.Loaded,
        isFundingOptionHistoryLoaded: state => state.fundingOptionHistoryState === FetchState.Loaded,
        enrollmentOptionLoaded: state => state.enrollmentOptionState === FetchState.Loaded,
    },
    actions: {
        setEmployer (employerId?: number) {
            const currentEmployerId = this.employerInformation.employerId;
            this.allEmployers[currentEmployerId] = copyState(_.cloneDeep(pristineProfileEmployer), this);

            if (employerId) {
                const existingEmployerInfo = this.allEmployers[employerId];

                if (existingEmployerInfo) {
                    copyState(this, existingEmployerInfo);
                    return;
                }
            }

            copyState(this, _.cloneDeep(pristineProfileEmployer));
        },
        setEmployerInformation (employerInformation: ProfileInformation): void {
            this.employerInformation = employerInformation;
            this.employerInformationState = FetchState.Loaded;
        },
        setFundingOption (fundingOption: FundingOption): void {
            this.fundingOption = fundingOption;
        },
        setFundingOptionHistory (fundingOptionHistory: FundingOption[]): void {
            this.fundingOptionHistory = fundingOptionHistory;
            this.fundingOptionHistoryState = FetchState.Loaded;
        },
        setFundingOptionHistoryState (fetchState: FetchState): void {
            this.fundingOptionHistoryState = fetchState;
        },
        setEmployerInformationLoading (): void {
            this.employerInformationState = FetchState.Loading;
        },
        setEmployerInformationFetchstate (fetchState: FetchState): void {
            this.employerInformationState = fetchState;
        },
        setEmployerInformationComplete () {
            this.profileWizard.generalInformation.complete = true;
            this.profileWizard.currentStep = ProfileWizardSteps.GeneralInformation + 1;
        },
        setBankInformation (bankInformation: EmployerAccounts): void {
            this.bankInformation = bankInformation;
            this.bankInformationState = FetchState.Loaded;
        },
        setBankInformationLoading (): void {
            this.employerInformationState = FetchState.Loading;
        },
        setBankInformationFetchstate (fetchState: FetchState): void {
            this.bankInformationState = fetchState;
        },
        setPayrollInformation (payrollInformation: PayrollInformation): void {
            if (!payrollInformation.payrollCutOffDays) {
                payrollInformation.payrollCutOffDays = 5;
            }
            this.payrollInformation = {
                ...payrollInformation,
                payrollProviderName: (payrollInformation.payrollProviderName || "").trim()
            };
            this.payrollInformationState = FetchState.Loaded;
        },
        setPayrollContactInformation (payrollInformation: PayrollInformation): void {
            if (!payrollInformation.payrollCutOffDays) {
                payrollInformation.payrollCutOffDays = 5;
            }
            this.payrollInformation = {
                ...payrollInformation,
                payrollProviderName: (payrollInformation.payrollProviderName || "").trim()
            };
            this.payrollInformationState = FetchState.Loaded;
        },
        setPayrollInformationLoading (): void {
            this.payrollInformationState = FetchState.Loading;
        },
        setPayrollInformationComplete () {
            this.profileWizard.payrollInformation.complete = true;
            this.profileWizard.currentStep = ProfileWizardSteps.PayrollSetup + 1;
        },
        setPlanDetailInformationLoading (planDetailInformation: PlanDetailInformation): void {
            this.planDetailInformationState = FetchState.Loading;
        },
        setPlanDetailInformation (planDetailInformation: PlanDetailsInformation): void {
            if (planDetailInformation && planDetailInformation.employerPlanDetails &&
                planDetailInformation.employerPlanDetails.length) {
                this.planDetailInformation = planDetailInformation.employerPlanDetails;
                this.isPlanDateLocked = planDetailInformation.isDateLocked;
                this.isCurrentPlanEditLocked = planDetailInformation.isCurrentPlanEditLocked;
            }
            this.planDetailInformationState = FetchState.Loaded;
        },
        setPlanDetailInformationComplete () {
            this.profileWizard.planDetail.complete = true;
            this.profileWizard.currentStep = ProfileWizardSteps.PlanDetail + 1;
        },
        setCurrentStep (currentStep: ProfileWizardSteps) {
            this.profileWizard.currentStep = currentStep;
        },
        setProfileWizard (profileWizard: ProfileWizard): void {
            this.profileWizard = profileWizard;
            this.profileWizardState = FetchState.Loaded;
        },
        setEnrollmentMetrics (enrollmentData: DashboardEnrollment): void {
            this.enrollmentMetrics = enrollmentData;
            this.enrollmentState = FetchState.Loaded;
        },
        setEnrollmentLoading (): void {
            this.enrollmentState = FetchState.Loading;
        },
        setEnrollmentComplete (): void {
            this.enrollmentState = FetchState.Loaded;
        },
        setEnrollmentOption (enrollmentOption: EnrollmentOption): void {
            this.enrollmentOption = enrollmentOption;
            this.enrollmentOptionState = FetchState.Loaded;
        },
        setEnrollmentOptionState (fetchState: FetchState): void {
            this.enrollmentOptionState = fetchState;
        },
        completeEmployerInformation (): Promise<any> {
            this.setEmployerInformationComplete();
            return Promise.resolve();
        },
        getWexEmployerProfile (wexEmployerCode, wexInstanceCode) {
            return UserService.getWexEmployerProfile(wexEmployerCode, wexInstanceCode)
                .then(response => this.setWexEmployerProfile( response.data.employerProfile ))
            .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        setWexEmployerProfile (wexEmployerProfile: WexEmployerProfile) {
            this.wexEmployerProfile = wexEmployerProfile;
        },
        saveWexEmployerProfile (request: EmployerProfileRequest): Promise<any> {
            return UserService.saveWexEmployerProfile(request)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        saveProfileInformation (
            request: CompanyDetails): Promise<void> {
            const profileStore = useProfileStore();
            return UserService.saveProfileInformation(request.profileInformation)
                .then(() => {
                    this.setEmployerInformation(request.profileInformation);
                    if (request.bankInformation === null ||
                        areBankDetailsEquivalent(this.bankInformation, request.bankInformation)) return;
                    return BankService.putEmployerAccounts(request.profileInformation.employerId, {
                        fundingAccount: request.bankInformation.fundingAccount,
                        feeAccount: request.bankInformation.feeAccount,
                        authorize: true
                    });
                })
                .then(() => {
                    if (request.fundingOption) {
                        return UserService.saveFundingOption(request.profileInformation.employerId,
                                { fundingOption: request.fundingOption })
                            .then(() => {
                                this.setFundingOption(request.fundingOption);
                                profileStore.setWizardComplete(request.profileInformation.employerId);
                            });
                    }
                })
                .then(() => {
                    if (request.fundingOption) {
                        return PayrollService.saveFundingOption(request.profileInformation.employerId,
                            { fundingOption: request.fundingOption });
                    }
                })
                .then(() => {
                    if (request.enrollmentOption) {
                        return UserService.saveEnrollmentOption(
                                request.profileInformation.employerId, { enrollmentOption: request.enrollmentOption })
                            .then(() => {
                                this.setEnrollmentOption(request.enrollmentOption);
                            });
                    }
                })
                .then(() => {
                    this.setBankInformation(request.bankInformation);
                    this.notifierStore.setSuccess("Your information has been updated.");
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadProfileInformation (employerId: number): Promise<void> {
            if (this.employerInformationState === FetchState.Loaded) {
                return Promise.resolve();
            }
            this.setEmployerInformationLoading();
            this.setBankInformationLoading();

            const tryGetProfileInformation = resolve => UserService.getProfileInformation(employerId)
                .then(response => {
                    const profileInformation = response.data;
                    profileInformation.employerId = employerId;
                    return profileInformation;
                })
                .then((profileInformation: ProfileInformation) => {
                    return new Promise((resolve, reject) => {
                        let action: () => void;
                        let attempts = 5;
                        action = () => {
                            BankService.getEmployerAccounts(employerId)
                                .then(response => resolve({ profileInformation, bankAccountData: response.data }))
                                .catch(err => {
                                    attempts--;
                                    if (attempts > 0 && err && err.response && err.response.status === 403) {
                                        // user not there yet, keep trying
                                        window.setTimeout(action, 5000);
                                        return;
                                    }

                                    reject(err);
                                });
                        };

                        action();
                    });
                })
                .then(({ profileInformation, bankAccountData }) => {
                    this.setEmployerInformation(profileInformation);
                    this.setBankInformation(bankAccountData);
                });

            const maxNumberOfRetries = 5;
            let currentNumberOfRetries = 0;

            return new Promise((resolve, reject) => {
                let action: () => void;
                action = () => {
                    tryGetProfileInformation(() => {
                        resolve();
                    }).catch(err => {
                        if (err && err.response && err.response.status) {
                            const httpStatus = err.response.status;
                            if (httpStatus >= 400 && httpStatus < 500) {
                                // user not there yet, keep trying
                                currentNumberOfRetries = currentNumberOfRetries + 1;

                                if (currentNumberOfRetries < maxNumberOfRetries) {
                                    window.setTimeout(action, 5000);
                                    return;
                                }
                            }
                        }

                        this.setBankInformationFetchstate(FetchState.Uninitialized);
                        this.setEmployerInformationFetchstate(FetchState.Uninitialized);
                        storeHelper.handleError({ error: err, rethrow: true });
                        reject(err);
                    });
                };

                action();
            });
        },
        getPayrollInformation (employerId: number): Promise<any> {
            return new Promise((resolve, reject) => {
                if (this.payrollInformationState === FetchState.Loaded) {
                    return resolve(this.payrollInformation);
                }

                this.setPayrollInformationLoading();
                PayrollService.getPayrollInformation(employerId)
                    .then(response => {
                        if (!response) {
                            reject({ message: "There was a problem reaching our servers", payload: response });
                            return;
                        }

                        const payrollInformation = response.data;
                        payrollInformation.employerId = employerId;
                        this.setPayrollInformation(payrollInformation);
                        resolve(payrollInformation);
                    })
                    .catch(err => reject(err));
            });
        },
        savePayrollInformation (
            { request, suppressNotifierMessages }): Promise<any> {
            return PayrollService.savePayrollInformation(request)
                .then(response => {
                    this.setPayrollInformation(response.data);
                    if (!suppressNotifierMessages) {
                        this.notifierStore.setSuccess("Your information has been updated.");
                    }
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        savePayrollContactInformation (
            { request, suppressNotifierMessages }): Promise<any> {
            return PayrollService.savePayrollContactInformation(request)
                .then(response => {
                    this.setPayrollContactInformation(response.data);
                    if (!suppressNotifierMessages) {
                        this.notifierStore.setSuccess("Your information has been updated.");
                    }
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadFundingOption (employerId: number): Promise<any> {
            return new Promise((resolve, reject) => {
                UserService.getFundingOption(employerId)
                    .then(response => {
                        this.setFundingOption(response.data.fundingOption);
                    }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
        loadFundingOptionHistory (employerId: number): Promise<any> {
            this.setFundingOptionHistoryState(FetchState.Uninitialized);
            return new Promise((resolve, reject) => {
                if (this.fundingOptionHistoryState === FetchState.Loaded) {
                    return resolve(this.fundingOptionHistory);
                }
                this.setFundingOptionHistoryState(FetchState.Loading);
                UserService.getFundingOptionHistory(employerId)
                    .then(response => {
                        this.setFundingOptionHistory(response.data);
                    }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
        loadEnrollmentOption (employerId: number): Promise<any> {
            if (this.enrollmentOptionState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setEnrollmentOptionState(FetchState.Loading);
            return UserService.getEnrollmentOption(employerId)
                .then(response => {
                    this.setEnrollmentOption(response.data.enrollmentOption);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        getPlanDetailInformation (employerId: number): Promise<any> {
            return new Promise<void>((resolve, reject) => {
                if (this.planDetailInformationState === FetchState.Loaded) {
                    resolve();
                    return;
                }

                this.setPlanDetailInformationLoading();
                PayrollService.getPlanDetailInformation(employerId)
                    .then(response => {
                        if (!response) {
                            reject({ message: "There was a problem reaching our servers", payload: response });
                            return;
                        }

                        this.setPlanDetailInformation(response.data);
                        resolve();
                    }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
        savePlanDetailInformation (
            { employerId, request, suppressNotifierMessages })
            : Promise<any> {
            const planDetailsModel: PlanDetailsInformation = { employerPlanDetails: request };
            const profileStore = useProfileStore();
            return PayrollService.savePlanDetailInformation(employerId, planDetailsModel)
                .then(response => this.setPlanDetailInformation(response.data))
                .then(response => {
                    if (!suppressNotifierMessages) {
                        this.notifierStore.setSuccess("Your information has been updated.");
                    }
                })
                .then(response => profileStore.saveOnboardProgressComplete(
                    { employerId, onboardingStep: ProfileWizardSteps.PlanDetail }))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        saveOnboardProgressComplete (request:
                                         { employerId: number, onboardingStep: ProfileWizardSteps }): Promise<any> {
            return UserService.setEmployerWizardProgressComplete(request.employerId, request.onboardingStep)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        setWizardComplete (employerId: number): Promise<any> {
            const userStore = useUserStore();
            return UserService.setWizardComplete(employerId)
                .then(response => userStore.setEmployerOnboarded());
        },
        getWizardState (
            data: { employerId: number, edit: boolean }): Promise<any> {
            return new Promise((resolve, reject) => {
                if (this.profileWizardState === FetchState.Loaded) {
                    resolve(this.profileWizard);
                    return;
                }

                UserService.getWizardState(data.employerId)
                    .then(response => {
                        if (!response) {
                            reject({ message: "There was a problem reaching our servers", payload: response });
                            return;
                        }

                        const wizardData = response.data;
                        const profileWizard: ProfileWizard = { ...initialWizard };
                        profileWizard.generalInformation.complete = wizardData.wizardProgressSteps
                            .some(d => d.wizardStepNumber === ProfileWizardSteps.GeneralInformation);
                        profileWizard.planDetail.complete = wizardData.wizardProgressSteps
                            .some(d => d.wizardStepNumber === ProfileWizardSteps.PlanDetail);
                        profileWizard.payrollInformation.complete = wizardData.wizardProgressSteps
                            .some(d => d.wizardStepNumber === ProfileWizardSteps.PayrollSetup);

                        if (!data.edit) profileWizard.currentStep = wizardData.currentStep;

                        this.setProfileWizard(profileWizard);
                        resolve(profileWizard);
                    }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
        loadEnrollmentMetrics (employerId: number): Promise<any> {
            return new Promise<void>((resolve, reject) => {
                if (this.enrollmentState === FetchState.Loaded) {
                    resolve();
                    return;
                }

                this.setEnrollmentLoading();
                UserService.getEmployerDashboard(employerId)
                    .then(response => {
                        if (!response) {
                            reject({ message: "There was a problem reaching our servers", payload: response });
                            return;
                        }

                        this.setEnrollmentMetrics(response.data.enrollment);
                        resolve();
                    }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
    }
});

const areBankDetailsEquivalent = (a: EmployerAccounts, b: EmployerAccounts) =>
    a.feeAccount.bankAccountNumber === b.feeAccount.bankAccountNumber
    && a.feeAccount.bankRoutingNumber === b.feeAccount.bankRoutingNumber
    && a.fundingAccount.bankAccountNumber === b.fundingAccount.bankAccountNumber
    && a.fundingAccount.bankRoutingNumber === b.fundingAccount.bankRoutingNumber;
